import { z } from 'zod';
import { ANALYSIS_TYPES, AUDIT_RESULTS, AUDIT_STATUSES, AUDIT_TEAM_ROLES, AUDIT_TYPES, CAPA_DAYS, COURSE_LEVELS, COURSE_TRACKS, CURRENCIES, DELIVERY_MODES, ENROLMENT_STATUSES, FINDING_SEVERITIES, FINDING_STATUSES, INVOICE_STATUSES, LAB_VERDICTS, MILESTONE_STATUSES, PAYMENT_METHODS, PROJECT_STATUSES, QUOTATION_STATUSES, SAMPLE_STATUSES, SESSION_STATUSES, STANDARDS, } from '../enums.js';
import { optionalText, patchSchemaOf } from './common.js';
// ---------------------------------------------------------------- audits
export const auditInputSchema = z.object({
    companyId: z.string().uuid(),
    type: z.enum(AUDIT_TYPES).default('internal'),
    standard: z.enum(STANDARDS).default('HACCP'),
    status: z.enum(AUDIT_STATUSES).default('scheduled'),
    scheduledDate: z.string().date(),
    completedDate: z.string().date().optional(),
    leadAuditorId: z.string().uuid().optional(),
    durationDays: z.coerce.number().int().min(1).max(60).default(1),
    scope: optionalText,
    result: z.enum(AUDIT_RESULTS).optional(),
    notes: optionalText,
});
export const auditUpdateSchema = patchSchemaOf(auditInputSchema);
/** Adding someone to an audit team. The lead lives on the audit itself. */
export const auditTeamMemberInputSchema = z.object({
    userId: z.string().uuid(),
    teamRole: z.enum(AUDIT_TEAM_ROLES).default('auditor'),
});
// -------------------------------------------------------------- findings
export const findingInputSchema = z.object({
    auditId: z.string().uuid(),
    severity: z.enum(FINDING_SEVERITIES).default('minor'),
    clause: optionalText,
    description: z.string().trim().min(3, 'Describe the non-conformity').max(4000),
    evidence: optionalText,
    status: z.enum(FINDING_STATUSES).default('open'),
    rootCause: optionalText,
    correctiveAction: optionalText,
    preventiveAction: optionalText,
    responsiblePerson: optionalText,
    /** Defaults from severity via CAPA_DAYS when omitted. */
    dueDate: z.string().date().optional(),
    closedDate: z.string().date().optional(),
});
export const findingUpdateSchema = patchSchemaOf(findingInputSchema).omit({ auditId: true });
/** The CAPA due date implied by a severity, counted from `from`. */
export function capaDueDate(severity, from = new Date()) {
    const due = new Date(from);
    due.setDate(due.getDate() + CAPA_DAYS[severity]);
    return due.toISOString().slice(0, 10);
}
// ---------------------------------------------------------- certificates
export const certificateInputSchema = z.object({
    companyId: z.string().uuid(),
    standard: z.enum(STANDARDS),
    certificateNumber: optionalText,
    certifyingBody: optionalText,
    issuedDate: z.string().date().optional(),
    expiryDate: z.string().date(),
    scope: optionalText,
    suspended: z.boolean().default(false),
});
export const certificateUpdateSchema = patchSchemaOf(certificateInputSchema);
// --------------------------------------------------------------- academy
export const courseInputSchema = z.object({
    code: z.string().trim().min(2).max(40),
    title: z.string().trim().min(3).max(200),
    titleAr: optionalText,
    standard: z.enum(STANDARDS).optional(),
    level: z.enum(COURSE_LEVELS).default('foundation'),
    track: z.enum(COURSE_TRACKS).optional(),
    durationHours: z.coerce.number().int().min(1).max(400).default(8),
    deliveryMode: z.enum(DELIVERY_MODES).default('in_person'),
    certificateValidityMonths: z.coerce.number().int().min(0).max(120).default(0),
    priceMinor: z.coerce.number().int().nonnegative().optional(),
    currency: z.enum(CURRENCIES).optional(),
    description: optionalText,
    active: z.boolean().default(true),
});
export const courseUpdateSchema = patchSchemaOf(courseInputSchema);
export const sessionInputSchema = z.object({
    courseId: z.string().uuid(),
    startDate: z.string().date(),
    endDate: z.string().date(),
    mode: z.enum(DELIVERY_MODES).default('in_person'),
    location: optionalText,
    trainerId: z.string().uuid().optional(),
    capacity: z.coerce.number().int().min(1).max(500).default(20),
    status: z.enum(SESSION_STATUSES).default('planned'),
    companyId: z.string().uuid().optional(),
    notes: optionalText,
}).refine((v) => v.startDate <= v.endDate, {
    message: 'The end date must be on or after the start date',
    path: ['endDate'],
});
export const sessionUpdateSchema = z.object({
    courseId: z.string().uuid().optional(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    mode: z.enum(DELIVERY_MODES).optional(),
    location: optionalText,
    trainerId: z.string().uuid().optional(),
    capacity: z.coerce.number().int().min(1).max(500).optional(),
    status: z.enum(SESSION_STATUSES).optional(),
    companyId: z.string().uuid().optional(),
    notes: optionalText,
});
export const traineeInputSchema = z.object({
    fullName: z.string().trim().min(2).max(140),
    fullNameAr: optionalText,
    email: z.union([z.string().trim().toLowerCase().email(), z.literal('')]).optional(),
    phone: optionalText,
    companyId: z.string().uuid().optional(),
    companyName: optionalText,
    jobTitle: optionalText,
});
export const traineeUpdateSchema = patchSchemaOf(traineeInputSchema);
export const enrolmentInputSchema = z.object({
    sessionId: z.string().uuid(),
    traineeId: z.string().uuid(),
    status: z.enum(ENROLMENT_STATUSES).default('registered'),
    enrolledDate: z.string().date().optional(),
    attendancePercent: z.coerce.number().int().min(0).max(100).optional(),
    examScore: z.coerce.number().int().min(0).max(100).optional(),
    passed: z.boolean().optional(),
    amountMinor: z.coerce.number().int().nonnegative().optional(),
    currency: z.enum(CURRENCIES).optional(),
});
export const enrolmentUpdateSchema = patchSchemaOf(enrolmentInputSchema).omit({
    sessionId: true, traineeId: true,
});
// ------------------------------------------------------------ laboratory
export const labSampleInputSchema = z.object({
    companyId: z.string().uuid().optional(),
    sampleName: z.string().trim().min(2).max(200),
    matrix: optionalText,
    status: z.enum(SAMPLE_STATUSES).default('received'),
    receivedDate: z.string().date(),
    completedDate: z.string().date().optional(),
    collectedBy: optionalText,
    notes: optionalText,
});
export const labSampleUpdateSchema = patchSchemaOf(labSampleInputSchema);
export const labTestInputSchema = z.object({
    sampleId: z.string().uuid(),
    analysisType: z.enum(ANALYSIS_TYPES),
    parameter: z.string().trim().min(1).max(200),
    method: optionalText,
    resultValue: optionalText,
    unit: optionalText,
    limitText: optionalText,
    verdict: z.enum(LAB_VERDICTS).optional(),
    completedDate: z.string().date().optional(),
});
export const labTestUpdateSchema = patchSchemaOf(labTestInputSchema).omit({ sampleId: true });
// ------------------------------------------------- quotations & invoices
export const documentLineSchema = z.object({
    description: z.string().trim().min(1, 'Describe the line').max(500),
    descriptionAr: optionalText,
    quantity: z.coerce.number().int().min(1).max(100_000).default(1),
    unitPriceMinor: z.coerce.number().int().nonnegative(),
});
export const quotationInputSchema = z.object({
    companyId: z.string().uuid(),
    dealId: z.string().uuid().optional(),
    status: z.enum(QUOTATION_STATUSES).default('draft'),
    currency: z.enum(CURRENCIES).default('USD'),
    issueDate: z.string().date(),
    validUntil: z.string().date().optional(),
    financialNumber: optionalText,
    personInCharge: optionalText,
    notes: optionalText,
    lines: z.array(documentLineSchema).min(1, 'Add at least one line'),
});
export const quotationUpdateSchema = patchSchemaOf(quotationInputSchema);
export const invoiceInputSchema = z.object({
    companyId: z.string().uuid(),
    quotationId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
    status: z.enum(INVOICE_STATUSES).default('draft'),
    currency: z.enum(CURRENCIES).default('USD'),
    issueDate: z.string().date(),
    dueDate: z.string().date().optional(),
    financialNumber: optionalText,
    personInCharge: optionalText,
    notes: optionalText,
    lines: z.array(documentLineSchema).min(1, 'Add at least one line'),
});
export const invoiceUpdateSchema = patchSchemaOf(invoiceInputSchema).extend({
    /**
     * MEFOSA's own invoice number.
     *
     * Not on the create schema: a new invoice always takes the next number in
     * the sequence, so nobody has to invent one. It is editable afterwards
     * because their real invoices carry their own numbering — "Invoice
     * #2025-xxx", "658", "LS162026" all appear in their books — and the
     * generated "#2026 27" is a placeholder until somebody corrects it.
     *
     * Trimmed, and never blank: an invoice with no number is one nobody can
     * quote back at you.
     */
    reference: z.string().trim().min(1, 'An invoice needs a number').max(60).optional(),
});
/**
 * The year and sequence inside a generated reference, if it is one.
 *
 * "#2026 27" is the scheme this system issues. When somebody edits a reference
 * INTO that shape, the sequence bookkeeping follows it, so the next automatic
 * number carries on past it rather than colliding with it. A reference in
 * MEFOSA's own format — "LS162026" — parses to null and leaves the sequence
 * alone, which is right: it is not part of this sequence.
 */
export function parseInvoiceReference(reference) {
    const match = reference.trim().match(/^#(\d{4})\s+(\d+)$/);
    if (!match)
        return null;
    return { refYear: Number(match[1]), refSeq: Number(match[2]) };
}
export const paymentInputSchema = z.object({
    invoiceId: z.string().uuid(),
    amountMinor: z.coerce.number().int().positive(),
    /**
     * The INVOICE's currency. The server refuses anything else.
     *
     * `paid` is summed in minor units and subtracted from the invoice total to
     * get the balance, so a payment in another currency would take fils off
     * dollars — a number that is not owed in any currency (rule 5). A client who
     * genuinely settles in another currency is a conversion somebody has to
     * decide on, and nobody has decided on one.
     */
    currency: z.enum(CURRENCIES),
    method: z.enum(PAYMENT_METHODS).default('bank_transfer'),
    paidOn: z.string().date(),
    reference: optionalText,
    notes: optionalText,
});
/**
 * Correcting a payment that is already recorded.
 *
 * Built from its own object rather than from `paymentInputSchema`, so that
 * two fields are structurally absent rather than merely ignored:
 *
 * `invoiceId` — moving a payment between invoices changes TWO balances at
 * once, and the screen it was edited from would show only one of them. To put
 * a payment on a different invoice, reverse it and record it there.
 *
 * `currency` — it is the invoice's, and changing it would re-denominate money
 * that has already been received (see above).
 */
export const paymentUpdateSchema = patchSchemaOf(z.object({
    amountMinor: z.coerce.number().int().positive(),
    method: z.enum(PAYMENT_METHODS).default('bank_transfer'),
    paidOn: z.string().date(),
    reference: optionalText,
    notes: optionalText,
}));
/** Line totals and document total. There is NO VAT - see docs/DECISIONS.md §2. */
export function lineTotalMinor(line) {
    return line.quantity * line.unitPriceMinor;
}
export function documentTotalMinor(lines) {
    return lines.reduce((total, l) => total + lineTotalMinor(l), 0);
}
// --------------------------------------------------------------- trading
export const productInputSchema = z.object({
    sku: optionalText,
    stockCode: optionalText,
    name: z.string().trim().min(2).max(200),
    nameAr: optionalText,
    brand: optionalText,
    category: optionalText,
    unit: optionalText,
    costMinor: z.coerce.number().int().nonnegative().optional(),
    priceMinor: z.coerce.number().int().nonnegative().optional(),
    currency: z.enum(CURRENCIES).default('USD'),
    // No quantity here on purpose. Stock arrives through a receipt, which
    // records the lot, the expiry and what it cost; on-hand is the sum of what
    // those receipts have left. A product is a description of a thing, not a
    // count of it.
    supplierId: z.string().uuid().optional(),
    storageLocation: optionalText,
    reorderLevel: z.coerce.number().int().nonnegative().default(0),
    active: z.boolean().default(true),
});
export const productUpdateSchema = patchSchemaOf(productInputSchema);
// -------------------------------------------------------- donor projects
export const donorInputSchema = z.object({
    name: z.string().trim().min(2).max(160),
    nameAr: optionalText,
    acronym: optionalText,
    website: optionalText,
    notes: optionalText,
});
export const projectInputSchema = z.object({
    name: z.string().trim().min(3).max(240),
    nameAr: optionalText,
    donorId: z.string().uuid().optional(),
    status: z.enum(PROJECT_STATUSES).default('pipeline'),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    budgetMinor: z.coerce.number().int().nonnegative().optional(),
    currency: z.enum(CURRENCIES).optional(),
    location: optionalText,
    beneficiaries: optionalText,
    description: optionalText,
    managerId: z.string().uuid().optional(),
});
export const projectUpdateSchema = patchSchemaOf(projectInputSchema);
export const milestoneInputSchema = z.object({
    projectId: z.string().uuid(),
    title: z.string().trim().min(2).max(240),
    isReportingDeadline: z.boolean().default(false),
    dueDate: z.string().date(),
    status: z.enum(MILESTONE_STATUSES).default('pending'),
    completedDate: z.string().date().optional(),
    notes: optionalText,
});
export const milestoneUpdateSchema = patchSchemaOf(milestoneInputSchema).omit({ projectId: true });
// ------------------------------------------------------------- inventory
/** Who MEFOSA buys stock from. */
export const supplierInputSchema = z.object({
    name: z.string().trim().min(2, 'Supplier name is required').max(160),
    nameAr: optionalText,
    contactName: optionalText,
    phone: optionalText,
    email: optionalText,
    country: optionalText,
    notes: optionalText,
    active: z.boolean().default(true),
});
export const supplierUpdateSchema = patchSchemaOf(supplierInputSchema);
/**
 * Receiving a delivery.
 *
 * Every receipt is its own batch, with its own lot number, expiry and cost —
 * two deliveries of the same plates are not interchangeable.
 */
export const receiveStockSchema = z.object({
    quantity: z.coerce.number().int().positive('Receive at least one'),
    batchNumber: optionalText,
    expiryDate: z.string().date().optional(),
    supplierId: z.string().uuid().optional(),
    purchaseReference: optionalText,
    receivedDate: z.string().date().optional(),
    unitCostMajor: z.coerce.number().nonnegative().optional(),
    currency: z.enum(CURRENCIES).default('USD'),
    notes: optionalText,
});
/**
 * Taking stock off the shelf.
 *
 * The reason decides what else is asked for: a sale needs a customer, a
 * write-off needs an explanation. Batches are chosen by the server, soonest
 * expiry first — that is not a decision to leave to whoever is typing.
 */
export const issueStockSchema = z.object({
    quantity: z.coerce.number().int().positive('Issue at least one'),
    reason: z.enum(['sale', 'internal_use', 'write_off']),
    companyId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
    usedOn: optionalText,
    reference: optionalText,
    note: optionalText,
}).refine((v) => v.reason !== 'write_off' || Boolean(v.note?.trim()), { path: ['note'], message: 'Say why this stock is being written off' }).refine((v) => v.reason !== 'sale' || Boolean(v.companyId), { path: ['companyId'], message: 'Choose the customer this went to' });
/** A stock-take correction, which may go either way and belongs to no batch. */
export const adjustStockSchema = z.object({
    batchId: z.string().uuid(),
    quantity: z.coerce.number().int().refine((n) => n !== 0, 'Quantity cannot be zero'),
    note: z.string().trim().min(1, 'Say why the count changed').max(500),
});
//# sourceMappingURL=modules.js.map